import { z } from 'zod';
import { patchOf } from './zod-setup';
import { amountSchema, currencyCodeSchema, dateOnlySchema, exchangeRateSchema } from './common';
import { colourSchema } from './classification';
import { TAG_MATCH_MODES } from './tags';
import { advancedFilterQueryFragment } from './record-filters';
/**
 * An expense that is in the books, or one that has only been proposed.
 *
 * A `draft` is in NO figure - not the spend total, not the dashboard, not
 * input credit on a GST return, and not the review engine. It becomes real
 * when a person confirms it, and only then.
 */
export const EXPENSE_STATUSES = ['recorded', 'draft'];
/** Expenses - what the user asked for first, after invoices. */
const expenseBase = z.object({
    date: dateOnlySchema,
    categoryId: z.string().trim().min(1, 'Pick a category').max(36),
    amount: amountSchema,
    currency: currencyCodeSchema.optional(),
    /** Base units per ONE unit of the expense currency. Required when the currency is not the base. */
    exchangeRate: exchangeRateSchema.optional(),
    taxId: z.string().trim().max(36).nullable().optional(),
    /**
     * Whether `amount` already includes the tax. Receipts are almost always
     * tax-inclusive, so this defaults differently from an invoice line - the
     * figure someone types off a till slip is the gross one.
     */
    taxInclusive: z.boolean().default(true),
    vendorId: z.string().trim().max(36).nullable().optional(),
    /** Which account it was paid from. This is what moves a bank balance. */
    paidThroughAccountId: z.string().trim().min(1, 'Say which account this was paid from').max(36),
    /**
     * Billable expenses can be pulled onto a customer's invoice later. A
     * customer is required for one, because "billable to nobody" is a state
     * that silently never gets invoiced.
     */
    isBillable: z.boolean().default(false),
    customerId: z.string().trim().max(36).nullable().optional(),
    projectId: z.string().trim().max(36).nullable().optional(),
    /** Marked up before it reaches the invoice. 10 means +10%. */
    markupPercent: z.number().min(0).max(1000).optional(),
    referenceNumber: z.string().trim().max(120).optional(),
    notes: z.string().trim().max(4000).optional(),
    /** The receipt image or PDF. The proof the expense happened. */
    receiptFileId: z.string().trim().max(36).nullable().optional(),
    customFields: z.record(z.string(), z.unknown()).optional(),
    /**
     * The labels on this expense. Replaced wholesale, like the dedicated
     * endpoint - see `setRecordTagsSchema` for why a set rather than a delta.
     *
     * Accepted here as well as on `PUT /expenses/:id/tags` because tagging while
     * filing is the ordinary path; the dedicated endpoint exists for the record
     * whose own form a period lock refuses.
     */
    tagIds: z
        .array(z.string().trim().min(1).max(36))
        .max(10, 'You can put ten tags on a record')
        .transform((ids) => Array.from(new Set(ids)))
        .optional(),
    /**
     * `draft` proposes the expense without putting it in the books.
     *
     * Defaults to `recorded`, so every caller that predates drafts keeps
     * recording spend. The one caller that sets `draft` is the MCP tool: Claude
     * reads a photographed bill, proposes it, and a person confirms it.
     */
    status: z.enum(EXPENSE_STATUSES).optional(),
});
/** Billable-needs-a-customer, applied to both create and update. */
const billableNeedsCustomer = (v, ctx) => {
    if (v.isBillable && !v.customerId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['customerId'],
            message: 'A billable expense needs a customer - otherwise there is nobody to bill it to.',
        });
    }
};
export const createExpenseSchema = expenseBase.strict().superRefine(billableNeedsCustomer);
/**
 * Built from the base object, not from createExpenseSchema, so the refinement
 * is added once, after the fields are made optional - with `patchOf`, so a
 * field that was not sent stays absent rather than taking its create-time
 * default (zod 4 - see zod-setup.ts).
 *
 * ── AND `status` IS OMITTED, WHICH IS NOT COSMETIC ──
 * Inherited from the base it would let a PATCH flip a draft to `recorded` -
 * reaching the effect of confirming it while skipping both things confirming
 * does: writing the bank transaction that `create` deliberately did not, and
 * re-checking that the period is still open. The result would be an expense
 * inside a filed return with no matching bank row, which reconciles against
 * nothing and is found months later.
 *
 * Becoming spend is an act, not a field edit. `POST /expenses/:id/confirm`.
 */
export const updateExpenseSchema = patchOf(expenseBase.omit({ status: true }))
    .strict()
    .superRefine(billableNeedsCustomer);
/**
 * A mileage expense: distance x rate, rather than an amount.
 *
 * Modelled as its own input because the user enters a distance and the AMOUNT
 * is derived. Letting someone type both is how a claim ends up with 100km at
 * ₹12/km recorded as ₹1,000.
 */
export const createMileageExpenseSchema = z
    .object({
    date: dateOnlySchema,
    categoryId: z.string().trim().min(1).max(36),
    distance: z.number().positive('Enter the distance travelled'),
    unit: z.enum(['km', 'mile']).default('km'),
    /** Per km/mile. Falls back to the org's configured rate for the date. */
    ratePerUnit: amountSchema.optional(),
    paidThroughAccountId: z.string().trim().min(1).max(36),
    vendorId: z.string().trim().max(36).nullable().optional(),
    isBillable: z.boolean().default(false),
    customerId: z.string().trim().max(36).nullable().optional(),
    projectId: z.string().trim().max(36).nullable().optional(),
    notes: z.string().trim().max(4000).optional(),
})
    .strict();
export const createExpenseCategorySchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).optional(),
    /**
     * Optional on create and assigned by rotation when absent - a colour is
     * what makes the dashboard legible, and demanding one from somebody adding
     * a category mid-way through filing a bill is a decision nobody wants.
     */
    colour: colourSchema.optional(),
    excludedFromTotals: z.boolean().optional(),
    interBook: z.boolean().optional(),
    emoji: z.string().trim().max(16).nullable().optional(),
    isActive: z.boolean().default(true),
})
    .strict();
/**
 * The categories a new org starts with.
 *
 * Chosen to cover what a small services business actually spends money on, so
 * the first expense can be filed without visiting settings. Not a chart of
 * accounts - Fint does not do double-entry - just the buckets a P&L needs.
 */
export const DEFAULT_EXPENSE_CATEGORIES = [
    'Advertising and marketing',
    'Bank fees and charges',
    'Consultant and professional fees',
    'Employee benefits',
    'Fuel and mileage',
    'IT and internet',
    'Meals and entertainment',
    'Office supplies',
    'Postage and courier',
    'Printing and stationery',
    'Rent',
    'Repairs and maintenance',
    'Salaries and wages',
    'Software subscriptions',
    'Telephone',
    'Travel',
    'Utilities',
    'Other expenses',
];
/**
 * The emoji each seeded expense category is born with, by name.
 *
 * ── THE MIGRATION IS THE SOURCE OF TRUTH ──
 * Migration 0015 stamped these onto every org that predates it, by NAME, in a
 * CASE expression; this table is that expression copied out, so an org
 * provisioned after the migration and an org repaired by it look the same.
 * Change one and change the other. Keyed by name rather than by index so a
 * category somebody renamed keeps whatever they set and gets nothing here.
 *
 * Why an emoji at all: the quick-add screen is meant for somebody who does
 * not read the words - a picker with a picture on every tile is what "a
 * ten-year-old can file an expense" means in practice.
 */
export const DEFAULT_EXPENSE_CATEGORY_EMOJI = {
    'Advertising and marketing': '📣',
    'Bank fees and charges': '🏦',
    'Consultant and professional fees': '🧑‍💼',
    'Employee benefits': '🎁',
    'Fuel and mileage': '⛽',
    'IT and internet': '💻',
    'Meals and entertainment': '🍽️',
    'Office supplies': '📎',
    'Postage and courier': '📦',
    'Printing and stationery': '🖨️',
    Rent: '🏢',
    'Repairs and maintenance': '🔧',
    'Salaries and wages': '👥',
    'Software subscriptions': '🧩',
    Telephone: '📞',
    Travel: '✈️',
    Utilities: '💡',
    'Other expenses': '🧾',
};
export const listExpensesQuerySchema = z
    .object({
    q: z.string().trim().max(200).optional(),
    /** One vendor's expenses (the vendor page), or `none` for those naming no vendor. */
    vendorId: z.string().trim().max(36).optional(),
    categoryId: z.string().trim().max(36).optional(),
    customerId: z.string().trim().max(36).optional(),
    accountId: z.string().trim().max(36).optional(),
    /**
     * A project id, or the literal `none` for "not on a project".
     *
     * `none` is a real option rather than an absent filter because "show me
     * what I have not classified yet" is the question a user asks on the day
     * they adopt projects, and it is what tells them their coverage is 23%.
     */
    projectId: z.string().trim().max(36).optional(),
    /**
     * Repeatable. `?tag=a&tag=b` arrives as an array from Express; a single
     * `?tag=a` arrives as a string, so both are accepted and normalised here
     * rather than in the controller.
     */
    tag: z
        .union([z.string().trim().max(36), z.array(z.string().trim().max(36))])
        .transform((v) => (Array.isArray(v) ? v : [v]))
        .pipe(z.array(z.string().min(1)).max(10))
        .optional(),
    tagMatch: z.enum(TAG_MATCH_MODES).default('any'),
    billable: z.enum(['all', 'billable', 'non_billable', 'unbilled', 'invoiced']).default('all'),
    /**
     * What is in the books, what has only been proposed, or both.
     *
     * Defaults to `recorded` rather than `all` on purpose: every caller that
     * existed before drafts did was asking for spend, and a default of `all`
     * would have put proposals into their answers without a line changing.
     */
    status: z.enum(['recorded', 'draft', 'all']).default('recorded'),
    from: dateOnlySchema.optional(),
    to: dateOnlySchema.optional(),
    sort: z.enum(['date', 'amount', 'category', 'vendor']).default('date'),
    dir: z.enum(['asc', 'desc']).default('desc'),
    cursor: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    ...advancedFilterQueryFragment,
})
    .strict();
/**
 * How many expenses one bulk classification may touch.
 *
 * ── WHY 500, AND NOT 50 OR 5,000 ──
 * The number is set by the slowest thing the operation does, which is tags: the
 * tag set on a record is REPLACED, one small transaction per record, because
 * `TagsService.replaceFor` is the join table's only write path and keeping it
 * that way is worth more than a hand-rolled multi-row insert here. Project and
 * category changes collapse into one UPDATE each for the whole batch, so they
 * are effectively free; tags are not. At 500 records the worst case - every one
 * of them getting a tag change - is a few hundred round trips, a couple of
 * seconds on a modest MySQL, and comfortably inside any reverse proxy's
 * default idle timeout.
 *
 * That timeout is the failure mode the cap exists to prevent, and it is worse
 * than it sounds: a request killed halfway leaves a batch PARTLY APPLIED with
 * nobody holding the response that says which half. The user then re-selects
 * and re-runs, and the second run's report is about a starting state they
 * cannot see. A refusal before the first write is recoverable; a silent
 * half-application is not.
 *
 * 500 is also large enough for the case this feature exists for: somebody who
 * has just imported a spreadsheet filters to one month, or one vendor, and
 * classifies the lot in a single action. A book of 600 needs two passes, which
 * the error message says plainly rather than leaving them to guess.
 */
export const BULK_CLASSIFY_MAX = 500;
/** Ten, as `setRecordTagsSchema` caps one record - a chip row is unreadable beyond it. */
export const MAX_TAGS_PER_RECORD = 10;
const bulkTagIds = z
    .array(z.string().trim().min(1).max(36))
    .max(MAX_TAGS_PER_RECORD, `You can add or remove ${MAX_TAGS_PER_RECORD} tags at a time`)
    .transform((ids) => Array.from(new Set(ids)));
/**
 * Classify many expenses at once.
 *
 * ── WHY IDS AND NOT A FILTER ──
 * "Apply this to everything matching my filters" is the obvious body and it is
 * the wrong one: the server would re-run the query, and between the moment the
 * screen rendered "47 matching" and the moment the request arrives, a colleague
 * can have filed two more expenses into that month. The user would then have
 * acted on 49 records having read the number 47, and no part of the exchange
 * would ever say so. Sending the ids means the request states exactly what it
 * acts on, the response can account for every one of them, and the audit rows
 * name records the user actually saw. `GET /expenses/ids` is what turns a
 * filter into that list, so "all matching" stays one click.
 *
 * ── WHY ADD AND REMOVE FOR TAGS, WHEN ONE RECORD REPLACES ──
 * `PUT /expenses/:id/tags` replaces the set, which is right when a person is
 * looking at that record's chips. Across a selection they are not: replacing
 * would delete every tag on every selected record that the user could not see
 * on screen - "Reimbursable" on three of forty rows, gone, reported as success.
 * A delta is the only shape that cannot destroy what was not being looked at.
 *
 * `setProject` is an object rather than a bare nullable field for the same
 * class of reason: `null` means "take these off their project", `undefined`
 * means "leave the project alone", and those are one keystroke apart in a
 * client that writes `projectId: form.value || null`. Wrapping it makes the
 * intent something the caller has to state.
 */
export const bulkClassifyExpensesSchema = z
    .object({
    expenseIds: z
        .array(z.string().trim().min(1).max(36))
        .min(1, 'Pick at least one expense')
        // Deduplicated BEFORE the cap, so a client that sends the same id twice
        // is not refused for a batch that is really under the limit.
        .transform((ids) => Array.from(new Set(ids)))
        .pipe(z
        .array(z.string())
        .max(BULK_CLASSIFY_MAX, `Bulk classification does ${BULK_CLASSIFY_MAX} expenses at a time. ` +
        'Narrow the filter - by month, or by account - and repeat.')),
    /** Present means "move these"; `projectId: null` means "take them off one". */
    setProject: z
        .object({ projectId: z.string().trim().max(36).nullable() })
        .strict()
        .optional(),
    /**
     * Who was paid: present means "these were paid to this vendor"; `vendorId:
     * null` takes the vendor off. A label, like the project - no figure moves -
     * so a filed period allows it.
     */
    setVendor: z
        .object({ vendorId: z.string().trim().max(36).nullable() })
        .strict()
        .optional(),
    /**
     * No null case: an expense always has a category, so there is no
     * "uncategorise these". Absent means leave it alone.
     */
    categoryId: z.string().trim().min(1).max(36).optional(),
    addTagIds: bulkTagIds.optional(),
    removeTagIds: bulkTagIds.optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    const acts = v.setProject !== undefined ||
        v.setVendor !== undefined ||
        v.categoryId !== undefined ||
        (v.addTagIds?.length ?? 0) > 0 ||
        (v.removeTagIds?.length ?? 0) > 0;
    if (!acts) {
        // A request that changes nothing would answer "500 updated" having
        // written nothing at all, which is the report a user trusts least once
        // they notice.
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Say what to change: a project, a vendor, a category, or tags to add or remove.',
        });
    }
    const both = (v.addTagIds ?? []).filter((id) => (v.removeTagIds ?? []).includes(id));
    if (both.length > 0) {
        // Whichever order the service applied them in would be an arbitrary
        // answer to a question the user did not mean to ask.
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['removeTagIds'],
            message: 'The same tag cannot be added and removed in one action.',
        });
    }
});
/** Why a record, or one field of it, was left alone. */
export const BULK_CLASSIFY_SKIP_REASONS = ['period_locked', 'tag_limit'];
/**
 * Money back on a charge (0030, requests.md R-04).
 *
 * The amount is what came BACK, as a positive figure - the refund is stored
 * negative, but nobody types a minus off a bank statement, and a decimal
 * keyboard on a phone often has no minus key to type one with. Everything
 * else - the category, the currency, the tax rate, the account - is the
 * refunded charge's, which is what makes the category total net.
 *
 * `bankTransactionId` names a credit already in the account (a statement
 * line), so the refund adopts it rather than writing a second one. Without
 * it, the refund writes the deposit itself.
 */
export const refundExpenseSchema = z
    .object({
    date: dateOnlySchema,
    amount: amountSchema,
    referenceNumber: z.string().trim().max(120).optional(),
    notes: z.string().trim().max(2000).optional(),
    bankTransactionId: z.string().trim().max(36).optional(),
})
    .strict();
//# sourceMappingURL=expenses.js.map